/**
 * Pure grid-time math for the week calendar.
 *
 * All functions operate in the BROWSER'S LOCAL timezone on purpose: the
 * therapist thinks in their own wall-clock time, and the API stores UTC.
 * Conversion to/from UTC happens only at the API boundary (Date.toISOString
 * on the way out, Date parsing of UTC ISO strings on the way in).
 */

export const SLOT_MIN = 15;

/** Monday-start array of 7 local midnights for the week containing `anchor`. */
export function weekDays(anchor: Date): Date[] {
  const local = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate());
  // getDay(): Sun=0..Sat=6 — shift so Monday is the week start.
  const sinceMonday = (local.getDay() + 6) % 7;
  const monday = new Date(local);
  monday.setDate(local.getDate() - sinceMonday);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
}

function parseHm(hm: string): { h: number; m: number } {
  const match = /^(\d{2}):(\d{2})$/.exec(hm);
  if (!match) throw new Error(`invalid HH:MM time: ${hm}`);
  return { h: Number(match[1]), m: Number(match[2]) };
}

/** Instant of slot `slotIdx` (15-min steps from `workStart`) on local `day`. */
export function slotToInstant(day: Date, workStart: string, slotIdx: number): Date {
  const { h, m } = parseHm(workStart);
  return new Date(
    day.getFullYear(), day.getMonth(), day.getDate(),
    h, m + slotIdx * SLOT_MIN,
  );
}

/** Inverse of slotToInstant (nearest slot; instants between slots snap down). */
export function instantToSlot(instant: Date, day: Date, workStart: string): number {
  const start = slotToInstant(day, workStart, 0);
  return Math.floor((instant.getTime() - start.getTime()) / (SLOT_MIN * 60_000));
}

/** Snap arbitrary minutes to the 15-min grid (nearest). */
export function snapMinutes(minutes: number): number {
  return Math.round(minutes / SLOT_MIN) * SLOT_MIN;
}

/** 24h HH:MM in local time. */
export function fmtHm(d: Date): string {
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/** Minutes between two instants (for block height / resize math). */
export function minutesBetween(a: Date, b: Date): number {
  return (b.getTime() - a.getTime()) / 60_000;
}
